/**
 * The page geometry, in one place.
 *
 * Header and body share one container width, so a toolbar ends exactly where
 * the rows beneath it end rather than running off to the left of them. That
 * only holds if the container and its padding are applied at the SAME level in
 * both — applied one level apart, the two end up 24px out of step, which is
 * how the meetings toolbar drifted before this was extracted.
 *
 * `both-edges` rather than plain `stable`: reserving the scrollbar gutter on
 * one side only moves the centre of the scroll box, which puts a centred list
 * half a scrollbar to the left of the header above it.
 */
export function PageFrame({
  title,
  subtitle,
  actions,
  toolbar,
  footer,
  width = 'wide',
  children,
}: {
  title: React.ReactNode
  /** Secondary, small, beside the title. Not a description under it. */
  subtitle?: React.ReactNode
  actions?: React.ReactNode
  toolbar?: React.ReactNode
  /**
   * `narrow` for a page that is a decision rather than a list — the external
   * share page is read top to bottom and its rows are one line each, and at
   * list width the checkbox and the badge beside it end up a hand apart.
   */
  width?: 'wide' | 'narrow'
  /**
   * A bar below the scroll box, in the same column as the body — for state that
   * belongs to the whole page rather than to whatever is currently in view.
   *
   * Outside the scroller rather than sticky inside it: an unsaved-changes bar
   * that a long form can push behind its own bottom padding is exactly the bar
   * that gets missed.
   */
  footer?: React.ReactNode
  children: React.ReactNode
}) {
  // One value, applied at all three levels — header, body and footer share the
  // column or they do not line up.
  const column = width === 'narrow' ? 'max-w-3xl' : 'max-w-6xl'

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 pt-6 pb-3">
        <div className={`mx-auto w-full ${column} px-6`}>
          <div className="mb-3 flex items-center gap-3">
            <h1 className="min-w-0 flex-1 text-lg font-semibold text-[var(--color-fg)]">
              {title}
              {subtitle ? (
                <span className="ml-2 text-xs font-normal text-[var(--color-fg-subtle)]">
                  {subtitle}
                </span>
              ) : null}
            </h1>
            {actions}
          </div>
          {toolbar}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable_both-edges]">
        <div className={`mx-auto w-full ${column} px-6 pb-10`}>{children}</div>
      </div>

      {footer ? (
        <div className="shrink-0 border-t border-[var(--color-border)] bg-[var(--color-surface)]">
          <div className={`mx-auto w-full ${column} px-6 py-2.5`}>{footer}</div>
        </div>
      ) : null}
    </div>
  )
}

/**
 * A group heading inside a page — "Projects", "Areas", "Archived".
 *
 * Sticky, and on the canvas colour rather than a surface, so it sits over the
 * scrolling list without the list appearing to run underneath a card.
 *
 * `-mx-6 px-6` makes the background full-bleed across the scroll box rather
 * than stopping at the content column. The column is narrower than the
 * scroller by the page gutter, and a row scrolling past through those two
 * strips is visible either side of the heading.
 */
export function SectionHeading({
  children,
  meta,
}: {
  children: React.ReactNode
  meta?: React.ReactNode
}) {
  return (
    <h2 className="sticky top-0 z-20 -mx-6 flex items-baseline gap-2 bg-[var(--color-canvas)] px-6 pt-3 pb-1.5 text-2xs font-medium text-[var(--color-fg-subtle)]">
      <span>{children}</span>
      {meta ? <span className="font-normal">{meta}</span> : null}
    </h2>
  )
}
